import { Answer, decode, encode, Packet, RECURSION_DESIRED } from 'dns-packet';

export interface EmailValidityStatus {
    abort: AbortController | null;
    hasFailedSimpleCheck: boolean;
    checkedEmail: string;
    isChecking: boolean;
    isValid: Promise<AdvancedCheckResults>;
}

type AdvancedCheckResults =
    | { valid: true; reason?: never }
    | { valid: false; reason: string }
    | { valid: null; reason: string }; // If this returns null the service is unavailable

export const DNS_OVER_HTTPS_URL = 'https://cloudflare-dns.com/dns-query';
let emailValidationCheck: EmailValidityStatus;
let emailValidationDebounceTimeout: ReturnType<typeof setTimeout> | null = null;

export function debouncedIsValidOrderEmail(
    email: string,
    delay = 300
): Promise<EmailValidityStatus> {
    // Stop previous attempts
    if (emailValidationDebounceTimeout !== null) {
        clearTimeout(emailValidationDebounceTimeout);
    }
    // Abort previous calls
    if (emailValidationCheck && emailValidationCheck.abort) {
        emailValidationCheck.abort.abort();
    }

    // First do a simple check, don't test when email is incomplete
    if (isValidOrderEmailSimple(email)) {
        return Promise.resolve({
            abort: null,
            checkedEmail: email,
            hasFailedSimpleCheck: true,
            isChecking: false,
            isValid: Promise.resolve({
                valid: false,
                reason: 'Email is incomplete',
            }),
        });
    }

    // Otherwise start debouncing the advanced call
    return new Promise(resolve => {
        return (emailValidationDebounceTimeout = setTimeout(() => {
            const advancedResult = isValidOrderEmailAdvanced(email);
            return resolve(advancedResult);
        }, delay));
    });
}

function isValidOrderEmailSimple(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return !emailRegex.test(email);
}

async function isValidOrderEmailAdvanced(
    email: string
): Promise<EmailValidityStatus> {
    const result: EmailValidityStatus = {
        abort: null,
        checkedEmail: email,
        hasFailedSimpleCheck: false,
        isChecking: true,
        isValid: Promise.resolve({ valid: false, reason: 'Unknown error' }), // temporary, will be overwritten
    };

    const splitEmail = email.split('@');
    if (splitEmail.length < 2) {
        result.hasFailedSimpleCheck = true;
        result.isValid = Promise.resolve({
            valid: false,
            reason: 'Email is not valid (No @ sign)',
        });
        return result;
    }

    const emailDomain = splitEmail.pop() || '';
    if (emailDomain.length < 3 || !emailDomain.includes('.')) {
        result.hasFailedSimpleCheck = true;
        result.isValid = Promise.resolve({
            valid: false,
            reason: 'Email is not valid (No domain part)',
        });
        return result;
    }

    const abortController = new AbortController();
    result.abort = abortController;

    result.isValid = fetchDOH(result, emailDomain.trim(), abortController);
    return result;
}

async function fetchDOH(
    result: EmailValidityStatus,
    emailDomain: string,
    abortController: AbortController
): Promise<AdvancedCheckResults> {
    try {
        const packet: Packet = {
            type: 'query',
            id: 1,
            flags: RECURSION_DESIRED,
            questions: [
                {
                    type: 'MX',
                    name: emailDomain,
                },
            ],
        };

        const response = await fetch(DNS_OVER_HTTPS_URL, {
            method: 'POST',
            body: encode(packet),
            headers: {
                'content-type': 'application/dns-message',
            },
            signal: abortController.signal,
        });

        if (!response.ok || !response.body) {
            throw 'DoH server failed to respond';
        }

        const arrayBuffer = await response.arrayBuffer();

        if (!arrayBuffer) {
            throw 'DoH server response is empty';
        }

        const dohResponse = decode(Buffer.from(arrayBuffer));
        result.isChecking = false;

        if (
            dohResponse &&
            dohResponse.answers &&
            Array.isArray(dohResponse.answers)
        ) {
            return validateDOHResponse(dohResponse.answers);
        } else {
            return {
                valid: null,
                reason: 'DoH server response not valid',
            };
        }
    } catch (err) {
        result.isChecking = false;
        return {
            valid: null,
            reason: typeof err === 'string' ? err : 'Unknown error',
        };
    }
}

function validateDOHResponse(answers: Answer[]): AdvancedCheckResults {
    if (answers.length == 0) {
        return {
            reason: 'No MX record found',
            valid: false,
        };
    }
    // If the mx record exists but is "nullified" it will be singular
    const answer = answers.shift();
    if (!answer) {
        return {
            reason: 'First MX record was empty',
            valid: false,
        };
    }
    if (
        'data' in answer &&
        typeof answer.data === 'object' &&
        'exchange' in answer.data
    ) {
        const exchange = answer.data.exchange.trim();
        const ip = exchange.match(
            /^(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
        );
        if (ip) {
            const ipToDecimal = (ipIn: string): number =>
                ipIn
                    .split('.')
                    .map(Number)
                    .reduce((acc, part) => (acc << 8) + part);

            // From - to (inclusive)
            // prettier-ignore
            const invalidRanges: [number, number][] = [
                [ipToDecimal('0.0.0.0'), ipToDecimal('0.255.255.255')],
                [ipToDecimal('10.0.0.0'), ipToDecimal('10.255.255.255')],
                [ipToDecimal('127.0.0.0'), ipToDecimal('127.255.255.255')],
                [ipToDecimal('169.254.0.0'), ipToDecimal('169.254.255.255')],
                [ipToDecimal('172.16.0.0'), ipToDecimal('172.31.255.255')],
                [ipToDecimal('169.254.0.0'), ipToDecimal('169.254.255.255')],
                [ipToDecimal('198.18.0.0'), ipToDecimal('198.19.255.255')],
                // Valid although looking weird
                // [ipToDecimal('192.0.0.0'), ipToDecimal('192.0.0.255')],
                [ipToDecimal('192.0.2.0'), ipToDecimal('192.0.2.255')],
                [ipToDecimal('198.51.100.0'), ipToDecimal('198.51.100.255')],
                [ipToDecimal('203.0.113.0'), ipToDecimal('203.0.113.255')],
                [ipToDecimal('192.168.0.0'), ipToDecimal('192.168.255.255')],
                [ipToDecimal('224.0.0.0'), ipToDecimal('239.255.255.255')],
                // We know this is still private, but could be public in the future
                // [ipToDecimal('240.0.0.0'), ipToDecimal('255.255.255.255')],
                [ipToDecimal('255.255.255.255'),ipToDecimal('255.255.255.255')],
            ];

            const ipDecimal = ipToDecimal(exchange);
            const res = !invalidRanges.some(
                ([start, end]) => ipDecimal >= start && ipDecimal <= end
            );
            if (res) {
                return { valid: true };
            }
            return {
                valid: res,
                reason: 'IP address (' + exchange + ') is not valid or private',
            };
        } else {
            const res = !['', '.', 'localhost'].includes(
                exchange.toLowerCase()
            );
            if (res) {
                return { valid: true };
            }
            return {
                valid: res,
                reason: 'Domain (' + exchange + ') is empty or private',
            };
        }
    }
    return {
        valid: false,
        reason: 'MX response had unexpected format',
    };
}
